import React, { useState, useEffect, useRef } from "react";
import {
  Handle,
  Position,
  NodeProps,
  useReactFlow,
  useEdges,
  useNodes,
} from "reactflow";
import {
  Trash2,
  Plus,
  ArrowUpDown,
  FlaskConical,
  GripVertical,
} from "lucide-react";
import { Input } from "@components/ui/input";
import { Button } from "@components/ui/button";
import { Switch } from "@components/ui/switch";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import Confirmation from "@components/common/ConfirmDialog";
import { toast } from "@components/ui/use-toast";
import { getConnectedInputs, getExecutionPath } from "../common/nodeDataFlow";
import { executeWorkflow } from "../createPluginApis";
import { ValidatedTitleInput } from "../components/ValidatedTitleInput";
import PaginatedInputViewer from "../common/PaginatedInputViewer";
import ResponseViewer from "../common/ResponseViewer";
import NodeContextMenu, {
  useNodeContextMenu,
  createContextMenuItems,
} from "../common/NodeContextMenu";
import CollapsedNodeView from "../common/NodeHeaderView";
import NodeContentSection from "../common/NodeContentSection";
import { FullscreenWrapper } from "../common/withFullscreen";
import { FullscreenToggleButton } from "../common/FullscreenToggleButton";
import { useResizablePanels } from "../hooks/useResizablePanels";
import { PanelDivider } from "../common/PanelDivider";
import {
  getNodeExecutionParameters,
  buildWorkflowPayload,
  handleNodeExecutionResults,
  findDownstreamNodes,
} from "../utils/nodeExecutionUtils";
import { getNodeBackendType } from "../utils/nodeTransformUtils";
import { handlerStyle, unwrapJsonData } from "../common/commonData";

interface SortField {
  id: string;
  field: string;
  order: "asc" | "desc";
}

interface SortNodeData {
  label?: string;
  header?: string;
  uid: string;
  title: string;
  description?: string;
  mode?: "simple" | "random" | "code";
  fields?: SortField[];
  customCode?: string;
  order?: "asc" | "desc";
  nullsLast?: boolean;
  disableDotNotation?: boolean;
  onDelete?: (id: string) => void;
  onDataChange?: (id: string, updates: any) => void;
  borderColor?: string;
  shadowColor?: string;
}

const SortNode: React.FC<NodeProps<SortNodeData>> = ({ id, data }) => {
  const label = data.label || data.header || "Sort";
  const [uid, setUid] = useState(data.uid || "");
  const [title, setTitle] = useState(data.title || "");
  const [description, setDescription] = useState(data.description || "");
  const [isTitleValid, setIsTitleValid] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Context menu
  const {
    showContextMenu,
    contextMenuPosition,
    handleContextMenu,
    closeContextMenu,
  } = useNodeContextMenu(id);

  // Mode state
  const [mode, setMode] = useState<"simple" | "random" | "code">(
    data.mode || "simple",
  );

  // Field mode state - start with one empty field if none provided
  const [fields, setFields] = useState<SortField[]>(
    data.fields && data.fields.length > 0
      ? data.fields
      : [
          {
            id: `field_${Date.now()}_initial`,
            field: "",
            order: "asc",
          },
        ],
  );

  // Custom mode state
  const [customCode, setCustomCode] = useState(data.customCode || "");
  const [order, setOrder] = useState<"asc" | "desc">(data.order || "asc");

  // Common options
  const [nullsLast, setNullsLast] = useState(data.nullsLast ?? true);
  const [disableDotNotation, setDisableDotNotation] = useState(
    data.disableDotNotation ?? true,
  );

  // Input/Output state
  const [inputData, setInputData] = useState<any[]>([]);
  const [outputData, setOutputData] = useState<any>(null);
  const [errorResponse, setErrorResponse] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const reactFlowInstance = useReactFlow();
  const { containerRef, widths, startDrag } = useResizablePanels([
    33.33, 33.33, 33.34,
  ]);

  // Ref to track previous outputData and prevent unnecessary updates
  const prevOutputDataRef = useRef<any>(null);

  // Get edges and nodes using hooks for proper reactivity
  const edges = useEdges();
  const nodes = useNodes();

  // Copy output to clipboard
  const handleCopyOutput = () => {
    const dataToCopy = errorResponse || outputData;
    if (!dataToCopy) return;
    navigator.clipboard.writeText(JSON.stringify(dataToCopy, null, 2));
    toast({
      variant: "success",
      title: "Copied!",
      description: "Output copied to clipboard.",
      duration: 2000,
    });
  };

  // Manual refresh function for InputViewer
  const refreshInputData = React.useCallback(() => {
    const inputs = getConnectedInputs(id, edges, nodes);
    setInputData(inputs);
  }, [id, edges, nodes]);

  // Get input data from connected nodes - use stringified comparison to avoid infinite loops
  useEffect(() => {
    const inputs = getConnectedInputs(id, edges, nodes);
    setInputData(inputs);
  }, [
    id,
    JSON.stringify(edges.filter((e) => e.target === id || e.source === id)),
    JSON.stringify(
      nodes.map((n) => ({
        id: n.id,
        configData: (n.data as any)?.configData,
        response: (n.data as any)?.response,
        outputData: (n.data as any)?.outputData,
      })),
    ),
  ]);

  // Sync outputData from node data (updated during workflow/node execution)
  useEffect(() => {
    const newOutputData = (data as any).outputData;

    if (!newOutputData) {
      if (outputData !== null && newOutputData === null) {
        setOutputData(null);
      }
      return;
    }

    if (prevOutputDataRef.current === newOutputData) {
      return;
    }

    const newDataStr = JSON.stringify(newOutputData);
    const prevDataStr = prevOutputDataRef.current
      ? JSON.stringify(prevOutputDataRef.current)
      : null;

    if (newDataStr !== prevDataStr) {
      prevOutputDataRef.current = newOutputData;
      setOutputData(newOutputData);
    } else {
      prevOutputDataRef.current = newOutputData;
    }
  }, [(data as any).outputData]);

  // Sync error from node data (set during global workflow execution)
  useEffect(() => {
    const executionStatus = (data as any).executionStatus;
    const error = (data as any).error;

    // If node failed during global execution, show error in ResponseViewer
    if (executionStatus === "failed" && error) {
      setErrorResponse(error);
    } else if (executionStatus === "success") {
      // Clear error when node succeeds
      setErrorResponse(null);
    }
  }, [(data as any).executionStatus, (data as any).error]);

  // Sync data changes with parent
  useEffect(() => {
    if (!data.onDataChange) return;

    // Build storedParameters based on mode
    // Map UI mode to backend mode
    const backendMode =
      mode === "simple" ? "field" : mode === "code" ? "custom" : "random";

    const storedParameters: any = {
      mode: backendMode,
      nullsLast,
      options: {
        disableDotNotation,
      },
    };

    if (mode === "simple") {
      storedParameters.fields = fields
        .filter((f) => f.field?.trim())
        .map((f) => ({
          field: f.field,
          order: f.order,
        }));
    } else if (mode === "code") {
      storedParameters.order = order;
      storedParameters.customCode = customCode;
    }

    data.onDataChange(id, {
      uid,
      title,
      description,
      mode,
      fields,
      customCode,
      order,
      nullsLast,
      disableDotNotation,
      outputData,
      response: outputData,
      sub_type: "sort_node",
      node_type: "data_filtration",
      storedParameters,
    });
  }, [
    uid,
    title,
    description,
    mode,
    fields,
    customCode,
    order,
    nullsLast,
    disableDotNotation,
    outputData,
    id,
  ]);

  // Generate UID on mount
  useEffect(() => {
    if (!uid) {
      const newUid = `sort_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      setUid(newUid);
    }
  }, [uid]);

  // Load saved parameters from data prop (when loading from backend)
  useEffect(() => {
    const params = (data as any).storedParameters || {};

    // Map backend mode to UI mode
    if (params.mode) {
      const uiMode =
        params.mode === "field"
          ? "simple"
          : params.mode === "custom"
            ? "code"
            : "random";
      setMode(uiMode as any);
    }
    if (params.nullsLast !== undefined) setNullsLast(params.nullsLast);
    if (params.options?.disableDotNotation !== undefined)
      setDisableDotNotation(params.options.disableDotNotation);

    // Load fields for field mode
    if (params.fields && Array.isArray(params.fields)) {
      const loadedFields = params.fields.map((f: any, index: number) => ({
        id: `field_${Date.now()}_${index}`,
        field: f.field || "",
        order: f.order || "asc",
      }));
      setFields(loadedFields);
    }

    // Load custom mode parameters
    if (params.customCode) setCustomCode(params.customCode);
    if (params.order) setOrder(params.order);
  }, []); // Only run once on mount

  // Execute sort operation
  const executeSortNode = async () => {
    if (!inputData?.length) {
      toast({
        variant: "destructive",
        title: "No Input Data",
        description: "Connect a node to provide input data",
      });
      return;
    }

    if (
      mode === "simple" &&
      fields.filter((f) => f.field.trim()).length === 0
    ) {
      toast({
        variant: "destructive",
        title: "No Sort Fields",
        description: "Add at least one sort field",
      });
      return;
    }

    if (mode === "code" && !customCode.trim()) {
      toast({
        variant: "destructive",
        title: "No Custom Code",
        description: "Enter custom sort code",
      });
      return;
    }

    if (!title.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Title is required for the sort node",
        duration: 3000,
      });
      return;
    }

    const pluginId = sessionStorage.getItem("editPluginId");

    if (!pluginId || pluginId === "temp-plugin") {
      toast({
        variant: "destructive",
        title: "Plugin Not Saved",
        description: "Please save the plugin first before testing",
        duration: 4000,
      });
      return;
    }

    const activeMeshFlowStr = sessionStorage.getItem("activeMeshFlow");
    if (!activeMeshFlowStr) {
      toast({
        variant: "destructive",
        title: "Error",
        description:
          "No active mesh flow. Please create or select a mesh flow first.",
        duration: 4000,
      });
      return;
    }

    setOutputData(null);
    setErrorResponse(null);
    setLoading(true);

    try {
      const activeMeshFlow = JSON.parse(activeMeshFlowStr);
      const allNodes = reactFlowInstance.getNodes();
      const allEdges = reactFlowInstance.getEdges();
      const executableNodes = getExecutionPath(id, allNodes, allEdges);

      // Mark all executable nodes as pending
      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          // Check if this node is in the execution path
          const isExecutable = executableNodes.some(
            (n: any) => n.id === node.id,
          );
          if (isExecutable) {
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "pending",
                executionResult: undefined,
                error: undefined,
              },
            };
          }
          return node;
        }),
      );

      // Execute workflow ONCE with all nodes up to and including this node
      const workflowNodes = executableNodes.map((node: any) => {
        let nodeType = "core";
        let subType = "http_request";
        let parameters;

        if (node.id === id) {
          // This is the current SortNode - build from local state
          // Map UI mode to backend mode
          const backendMode =
            mode === "simple" ? "field" : mode === "code" ? "custom" : "random";

          const sortParameters: any = {
            mode: backendMode,
            nullsLast,
            options: {
              disableDotNotation,
            },
          };

          if (mode === "simple") {
            sortParameters.fields = fields
              .filter((f) => f.field.trim())
              .map((f) => ({
                field: f.field,
                order: f.order,
              }));
          } else if (mode === "code") {
            sortParameters.order = order;
            sortParameters.customCode = customCode;
          }

          nodeType = "data_filtration";
          subType = "sort_node";
          parameters = {
            ...sortParameters,
            label: label,
          };
        } else {
          // Other nodes - use centralized utility (checks storedParameters first)
          // Determine backend type and sub_type using centralized mapping
          const backendTypeInfo = getNodeBackendType(node.type || "custom");
          nodeType = backendTypeInfo.type;
          subType = backendTypeInfo.sub_type;

          // Get parameters using centralized utility
          parameters = getNodeExecutionParameters(node, pluginId).parameters;
        }

        return {
          uid: node.data.uid || node.id,
          title: node.data.title || node.data.label || "Untitled",
          description: node.data.description || "",
          type: nodeType,
          sub_type: subType,
          version: "1.0",
          position: [node.position.x, node.position.y],
          parameters,
        };
      });

      // Build connections between nodes
      const nodeIdToUidMap: Record<string, string> = {};
      executableNodes.forEach((node: any) => {
        nodeIdToUidMap[node.id] = node.data.uid || node.id;
      });

      const executionPathNodeIds = new Set(
        executableNodes.map((n: any) => n.id),
      );

      const connections: any = {};
      allEdges
        .filter(
          (edge) =>
            executionPathNodeIds.has(edge.source) &&
            executionPathNodeIds.has(edge.target),
        )
        .forEach((edge) => {
          const sourceUid = nodeIdToUidMap[edge.source];
          const targetUid = nodeIdToUidMap[edge.target];

          if (!sourceUid || !targetUid) return;

          if (!connections[sourceUid]) {
            connections[sourceUid] = {
              main: [],
            };
          }

          const sourceNode = executableNodes.find(
            (n: any) => n.id === edge.source,
          );
          const isIfElseNode =
            sourceNode?.type === "ifElseNode" ||
            sourceNode?.data?.sub_type === "if_else";

          let outputIndex = 0;
          if (isIfElseNode && edge.sourceHandle) {
            outputIndex = edge.sourceHandle === "false" ? 1 : 0;
          }

          if (!connections[sourceUid].main[outputIndex]) {
            connections[sourceUid].main[outputIndex] = [];
          }

          connections[sourceUid].main[outputIndex].push({
            node: targetUid,
            type: "main",
            index: 0,
          });
        });

      const workflowPayload = buildWorkflowPayload(
        activeMeshFlow,
        workflowNodes,
        connections,
      );

      const result = await executeWorkflow(workflowPayload);

      // Update ALL executed nodes with their results (not just the clicked node)
      if (result?.results?.[0]?.node_results) {
        const nodeResults = result.results[0].node_results;

        // Use the utility function to handle node execution results
        const { hasErrors, errorMessage, errorNodeTitle } =
          handleNodeExecutionResults(reactFlowInstance, nodeResults, allEdges);

        // Extract this node's output for local state
        const resultsMap = new Map<string, any>();
        nodeResults.forEach((nodeResult: any) => {
          resultsMap.set(nodeResult.uid, nodeResult);
        });

        const currentNodeResult = resultsMap.get(uid || id);
        if (currentNodeResult) {
          // Check if this node failed
          const isFailed =
            currentNodeResult.status === "error" ||
            currentNodeResult.status === "failed" ||
            currentNodeResult.status === "failure";

          if (isFailed) {
            // Show error in ResponseViewer but don't pass to downstream nodes
            setErrorResponse(currentNodeResult.error || "Execution failed");
            setOutputData(null);
          } else if (currentNodeResult.output_data) {
            // Success - extract and display output data
            const responseData = unwrapJsonData(currentNodeResult.output_data);
            setOutputData(responseData);
            setErrorResponse(null);
          }
        }

        // Show appropriate toast based on execution result
        if (hasErrors) {
          // Show error toast with specific error message
          const errorDescription = errorMessage
            ? `${errorNodeTitle}: ${errorMessage}`
            : "One or more nodes failed during execution";

          const shortError =
            errorDescription.length > 150
              ? errorDescription.substring(0, 150) + "..."
              : errorDescription;

          toast({
            variant: "destructive",
            title: "Execution Failed",
            description: shortError,
            duration: 7000,
          });
        } else {
          // Only show success if ALL nodes succeeded
          toast({
            variant: "success",
            title: "Success",
            description: "Sort executed successfully",
            duration: 3000,
          });
        }
      } else {
        // Fallback - no node results in response
        // Check for success at top level
        const isSuccess =
          result?.success === true ||
          result?.status === "success" ||
          result?.body?.status === 1 ||
          result?.body?.status === "1" ||
          result?.status === 1;

        if (isSuccess) {
          // Mark current node as success
          reactFlowInstance.setNodes((nodes) =>
            nodes.map((node) => {
              if (node.id === id) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "success",
                  },
                };
              }
              return node;
            }),
          );

          toast({
            variant: "success",
            title: "Success",
            description: result?.message || "Sort executed successfully",
            duration: 3000,
          });
        } else {
          // Mark current node and all downstream nodes as failed
          const allEdges = reactFlowInstance.getEdges();
          const downstreamNodes = findDownstreamNodes(id, allEdges);

          reactFlowInstance.setNodes((nodes) =>
            nodes.map((node) => {
              if (node.id === id) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "failed",
                    error:
                      result?.body?.message ||
                      result?.message ||
                      "Execution failed",
                  },
                };
              } else if (downstreamNodes.has(node.id)) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "failed",
                    error: "Not executed due to upstream failure",
                  },
                };
              }
              return node;
            }),
          );

          toast({
            variant: "destructive",
            title: "Execution Failed",
            description:
              result?.body?.message ||
              result?.message ||
              "Sort execution failed",
            duration: 4000,
          });
        }
      }
    } catch (error: any) {
      console.error("Sort Execution Error:", error);
      setErrorResponse(error?.message || "Sort execution failed");
      setOutputData(null);

      // Get all edges for finding downstream nodes
      const allEdges = reactFlowInstance.getEdges();
      const downstreamNodes = findDownstreamNodes(id, allEdges);

      // Update current node and all downstream nodes to failed status
      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            // Mark the failed node as failed
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "failed",
                error: error?.message || "Sort execution failed",
              },
            };
          } else if (downstreamNodes.has(node.id)) {
            // Mark downstream nodes as failed (not executed due to upstream failure)
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "failed",
                error: "Not executed due to upstream failure",
              },
            };
          }
          return node;
        }),
      );

      toast({
        variant: "destructive",
        title: "Error",
        description: error?.message || "Sort execution failed",
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (nodeId: string) => {
    setDeleteId(nodeId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (deleteId && data.onDelete) {
      data.onDelete(deleteId);
    }
    setShowDeleteConfirm(false);
    setDeleteId(null);
  };

  const addField = () => {
    setFields([
      ...fields,
      {
        id: `field_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
        field: "",
        order: "asc",
      },
    ]);
  };

  const removeField = (fieldId: string) => {
    setFields(fields.filter((f) => f.id !== fieldId));
  };

  const updateField = (fieldId: string, updates: Partial<SortField>) => {
    setFields(fields.map((f) => (f.id === fieldId ? { ...f, ...updates } : f)));
  };

  // Handle field reordering
  const handleFieldDragStart = (e: React.DragEvent, index: number) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("fieldIndex", index.toString());

    const target = e.currentTarget.closest(".field-row");
    if (target) {
      target.classList.add("opacity-50");
    }
  };

  const handleFieldDragEnd = (e: React.DragEvent) => {
    const target = e.currentTarget.closest(".field-row");
    if (target) {
      target.classList.remove("opacity-50");
    }
  };

  const handleFieldDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData("fieldIndex"));

    if (dragIndex === dropIndex) return;

    const newFields = [...fields];
    const [draggedField] = newFields.splice(dragIndex, 1);
    newFields.splice(dropIndex, 0, draggedField);

    setFields(newFields);
  };

  // Handle drop on field input
  const handleDropOnFieldInput = (e: React.DragEvent, fieldId: string) => {
    e.preventDefault();
    e.stopPropagation();
    const dragData = JSON.parse(e.dataTransfer.getData("application/json"));

    // Normalize path: remove $json prefix, clean up dots
    const pathWithoutPrefix = dragData.path.replace(/^\$json\./, "");
    const normalizedPath = pathWithoutPrefix
      .replace(/\.+/g, ".")
      .replace(/^\.|\.$/g, "");

    // Find the current field value and append to it
    const currentField = fields.find((f) => f.id === fieldId);
    const currentValue = currentField?.field || "";

    updateField(fieldId, {
      field: `${currentValue}${normalizedPath}`,
    });
  };

  // Extract and merge input data from connected nodes
  const getDisplayData = (input: any) => {
    const EXCLUDE = [
      "uid",
      "isDecript",
      "isDecrypted",
      "title",
      "testConnectionBtn",
      "tabId",
      "configType",
      "onDelete",
      "onDataChange",
      "header",
      "borderColor",
      "shadowColor",
      "label",
      "nodeType",
      "itemCount",
    ];

    const extractJsonData = (data: any) => {
      if (Array.isArray(data)) {
        return data.map((item) => {
          if (item && typeof item === "object" && "json_data" in item) {
            return item.json_data;
          }
          return item;
        });
      }
      if (data && typeof data === "object" && "json_data" in data) {
        return data.json_data;
      }
      return data;
    };

    if (input.outputData) {
      const extracted = extractJsonData(input.outputData);
      return extracted;
    }

    if (input.response) {
      const extracted = extractJsonData(input.response);
      return extracted;
    }

    const displayData: any = {};
    if (input.configData) {
      const encryptedFields = input.configData.isDecript || [];

      Object.entries(input.configData)
        .filter(([key]) => !EXCLUDE.includes(key))
        .forEach(([key, value]) => {
          if (Array.isArray(encryptedFields) && encryptedFields.includes(key)) {
            displayData[key] = "********************";
          } else {
            displayData[key] = value;
          }
        });
    }
    return displayData;
  };

  // Render input data section
  const renderInputData = () => {
    if (!inputData?.length) {
      return (
        <div className="text-xs text-muted-foreground p-4 border rounded-md bg-muted/20 text-center">
          <div className="font-medium">No Input Connected</div>
          <div>Connect a node to see data here</div>
        </div>
      );
    }

    // Build sources array for PaginatedInputViewer (handles tabs internally)
    const sources = inputData.map((input, index) => {
      // Generate label: if from switch node, use "Rule X", otherwise use original label
      let displayLabel = input.label || `Source ${index + 1}`;

      // Check if label indicates switch node (e.g., "switch", "SWITCH")
      if (displayLabel.toLowerCase().includes("switch")) {
        displayLabel = `Rule ${index + 1}`;
      }

      return {
        label: displayLabel,
        data: getDisplayData(input),
        isConfig: !!input.configData, // Mark as config if source is configuration
      };
    });

    return (
      <PaginatedInputViewer
        sources={sources}
        onRefresh={refreshInputData}
        sourceCount={inputData.length}
      />
    );
  };

  return (
    <FullscreenWrapper
      id={id}
      isExpanded={isExpanded}
      executionStatus={(data as any).executionStatus}
      borderColor={data.borderColor}
      shadowColor={data.shadowColor}
      onContextMenu={handleContextMenu}
      expandedClassName="w-[90vw] !max-w-[1000px] flex flex-col"
    >
      <Confirmation
        text="Are you sure you want to delete this node?"
        confirmAction={confirmDelete}
        rejectAction={() => setShowDeleteConfirm(false)}
        confirmText="Confirm"
        rejectText="Cancel"
        show={showDeleteConfirm}
        closeDialog={() => setShowDeleteConfirm(false)}
      />

      {/* Context Menu */}
      <NodeContextMenu
        show={showContextMenu}
        position={contextMenuPosition}
        onClose={closeContextMenu}
        items={[
          createContextMenuItems.expandCollapse(isExpanded, () =>
            setIsExpanded((prev) => !prev),
          ),
          createContextMenuItems.delete(
            () => handleDelete(id),
            !!data.onDelete,
          ),
        ]}
      />

      {/* Handles */}
      <Handle type="target" position={Position.Left} className={handlerStyle} />
      <Handle
        type="source"
        position={Position.Right}
        className={handlerStyle}
      />

      {/* Header */}
      <div
        className={`flex items-center justify-between gap-4 rounded-md transition-all duration-500 flex-shrink-0 ${
          isExpanded ? "p-3" : "!p-3"
        }`}
      >
        <CollapsedNodeView
          icon={
            <ArrowUpDown
              className={
                (data as any).executionStatus === "running" ||
                (data as any).executionStatus === "pending"
                  ? "animate-bounce"
                  : ""
              }
            />
          }
          label={label}
          title={title}
          iconSize={35}
          borderColor={data.borderColor}
          onToggle={() => setIsExpanded((prev) => !prev)}
          isExpanded={isExpanded}
          executionStatus={(data as any).executionStatus}
          description={description}
        />
        <FullscreenToggleButton isExpanded={isExpanded} />
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {/* Metadata Form */}
          <div className="mt-2 p-4 border-t flex items-start gap-4 flex-shrink-0">
            <div className="space-y-1 w-1/2">
              <label className="text-xs font-medium text-muted-foreground">
                Node Title <span className="text-primary">*</span>
              </label>
              <ValidatedTitleInput
                id={id}
                title={title}
                onChange={setTitle}
                nodes={nodes}
                dataTitle={data.title}
                onValidationChange={setIsTitleValid}
              />
            </div>
            <div className="space-y-1 w-1/2">
              <label className="text-xs font-medium text-muted-foreground">
                Node Description (optional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter node description"
                className="w-full p-2 border rounded text-xs resize-none text-muted-foreground bg-background"
                rows={1}
              />
            </div>
          </div>

          {/* 3 Section Grid */}
          <div
            ref={containerRef}
            className="flex gap-0 p-4 border-t flex-1 min-h-0 overflow-hidden"
          >
            {/* Left Section - Input Data (4/12 columns) */}
            <div
              style={{ width: `${widths[0]}%`, minWidth: 80, flexShrink: 0 }}
            >
              <NodeContentSection scrollable={false} hasRelative={true}>
                {renderInputData()}
              </NodeContentSection>
            </div>

            <PanelDivider onMouseDown={startDrag(0)} />

            {/* Middle Section - Config (4/12 columns) */}
            <div
              style={{ width: `${widths[1]}%`, minWidth: 80, flexShrink: 0 }}
            >
              <NodeContentSection hasRelative={true} scrollable={false}>
                <div className="flex-1 min-h-0 flex flex-col">
                  {/* Single scroll surface — wraps all scrollable content */}
                  <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col gap-2 relative">
                    <h3 className="text-sm font-semibold sticky top-0 bg-background z-10 pb-2 border-b">
                      Parameters
                    </h3>

                    {/* Mode Selection */}
                    <div className="space-y-2 border rounded-md p-3 bg-muted/20 mt-4">
                      <label className="text-xs font-semibold">Sort Mode</label>
                      <Select
                        value={mode}
                        onValueChange={(value: any) => setMode(value)}
                      >
                        <SelectTrigger className="w-full text-xs h-8">
                          <SelectValue placeholder="Select mode" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectItem value="simple" className="text-xs">
                              Simple
                            </SelectItem>
                            <SelectItem value="random" className="text-xs">
                              Random
                            </SelectItem>
                            <SelectItem value="code" className="text-xs">
                              Code
                            </SelectItem>
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </div>

                    <h3 className="text-sm font-semibold sticky top-0 bg-background z-10 pb-2 border-b">
                      Sort Configuration
                    </h3>

                    {/* Simple Mode */}
                    {mode === "simple" && (
                      <div className="space-y-2">
                        {fields.length > 0 && (
                          <div className="space-y-2 pt-1">
                            {fields.map((field, index) => (
                              <div
                                key={field.id}
                                className="field-row flex items-start gap-2 transition-all rounded-md"
                                onDragOver={(e) => {
                                  if (
                                    e.dataTransfer.types.includes("fieldindex")
                                  ) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    e.dataTransfer.dropEffect = "move";
                                    e.currentTarget.classList.add(
                                      "bg-primary/10",
                                      "border-2",
                                      "border-primary",
                                      "border-dashed",
                                    );
                                  }
                                }}
                                onDragLeave={(e) => {
                                  if (
                                    e.dataTransfer.types.includes("fieldindex")
                                  ) {
                                    e.currentTarget.classList.remove(
                                      "bg-primary/10",
                                      "border-2",
                                      "border-primary",
                                      "border-dashed",
                                    );
                                  }
                                }}
                                onDrop={(e) => {
                                  if (e.dataTransfer.getData("fieldIndex")) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    e.currentTarget.classList.remove(
                                      "bg-primary/10",
                                      "border-2",
                                      "border-primary",
                                      "border-dashed",
                                    );
                                    handleFieldDrop(e, index);
                                  }
                                }}
                              >
                                {/* Drag Handle + Delete */}
                                <div className="flex flex-col items-center gap-2">
                                  <div
                                    draggable
                                    onDragStart={(e) =>
                                      handleFieldDragStart(e, index)
                                    }
                                    onDragEnd={handleFieldDragEnd}
                                    className="cursor-grab active:cursor-grabbing pt-2 rounded hover:bg-muted/50 transition-all nodrag"
                                  >
                                    <GripVertical
                                      size={20}
                                      className="text-muted-foreground hover:text-primary transition"
                                    />
                                  </div>

                                  <Trash2
                                    size={16}
                                    className="cursor-pointer text-red-500 hover:text-red-700 transition"
                                    onClick={() => removeField(field.id)}
                                  />
                                </div>

                                {/* Field Card */}
                                <div className="flex-1 border rounded-md p-2 space-y-2 bg-background">
                                  {/* Field Name */}
                                  <div className="space-y-1">
                                    <label className="text-xs text-muted-foreground">
                                      Field Path
                                    </label>
                                    <div
                                      onDragOver={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        e.currentTarget.classList.add(
                                          "ring-2",
                                          "ring-primary",
                                          "bg-primary/5",
                                        );
                                      }}
                                      onDragLeave={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        e.currentTarget.classList.remove(
                                          "ring-2",
                                          "ring-primary",
                                          "bg-primary/5",
                                        );
                                      }}
                                      onDrop={(e) => {
                                        e.currentTarget.classList.remove(
                                          "ring-2",
                                          "ring-primary",
                                          "bg-primary/5",
                                        );
                                        handleDropOnFieldInput(e, field.id);
                                      }}
                                      className="w-full border border-transparent rounded transition-all"
                                    >
                                      <Input
                                        value={field.field}
                                        onChange={(e) =>
                                          updateField(field.id, {
                                            field: e.target.value,
                                          })
                                        }
                                        placeholder="e.g., address.city or drag field"
                                        className="text-xs h-7"
                                      />
                                    </div>
                                  </div>

                                  {/* Sort Order */}
                                  <div className="space-y-1">
                                    <label className="text-xs text-muted-foreground">
                                      Order
                                    </label>
                                    <Select
                                      value={field.order}
                                      onValueChange={(value: "asc" | "desc") =>
                                        updateField(field.id, { order: value })
                                      }
                                    >
                                      <SelectTrigger className="w-full text-xs h-7">
                                        <SelectValue placeholder="Select order" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectGroup>
                                          <SelectItem
                                            value="asc"
                                            className="text-xs"
                                          >
                                            Ascending
                                          </SelectItem>
                                          <SelectItem
                                            value="desc"
                                            className="text-xs"
                                          >
                                            Descending
                                          </SelectItem>
                                        </SelectGroup>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Add Field Button */}
                        <div className="border-2 border-dashed rounded-md p-4 text-center space-y-2">
                          <p className="text-xs text-muted-foreground">
                            Need to sort by multiple fields?
                          </p>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={addField}
                            className="text-xs"
                          >
                            <Plus size={12} className="mr-1" /> Add Fields to
                            sort by
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Random Mode */}
                    {mode === "random" && (
                      <div className="p-4 border rounded-md bg-muted/20 text-center">
                        <div className="text-sm font-medium mb-2">
                          Random Sort
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Items will be sorted in random order. No additional
                          configuration needed.
                        </div>
                      </div>
                    )}

                    {/* Code Mode */}
                    {mode === "code" && (
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <label className="text-xs font-medium text-muted-foreground">
                            Custom Expression{" "}
                            <span className="text-primary">*</span>
                          </label>
                          <textarea
                            value={customCode}
                            onChange={(e) => setCustomCode(e.target.value)}
                            placeholder="e.g., item['price'] * item['quantity']"
                            className="w-full p-2 border rounded text-xs font-mono resize-none text-foreground bg-background h-20"
                          />
                          <div className="text-xs text-muted-foreground">
                            Enter a Python expression to compute sort value for
                            each item
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label className="text-xs font-medium text-muted-foreground">
                            Sort Order
                          </label>
                          <Select
                            value={order}
                            onValueChange={(value: "asc" | "desc") =>
                              setOrder(value)
                            }
                          >
                            <SelectTrigger className="w-full text-xs h-8">
                              <SelectValue placeholder="Select order" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectGroup>
                                <SelectItem value="asc" className="text-xs">
                                  Ascending
                                </SelectItem>
                                <SelectItem value="desc" className="text-xs">
                                  Descending
                                </SelectItem>
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}

                    {/* Common Options */}
                    <h4 className="text-xs font-semibold mt-3 mb-2 border-t pt-3">
                      Options
                    </h4>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-2 bg-muted/20 rounded">
                        <span className="text-xs font-medium">
                          Disable Dot Notation
                        </span>
                        <Switch
                          checked={disableDotNotation}
                          onCheckedChange={setDisableDotNotation}
                          className="scale-75"
                        />
                      </div>
                    </div>
                  </div>
                  {/* Execute Button */}
                  <div className="sticky -bottom-1 left-0 right-0 bg-background border-t">
                    <Button
                      onClick={executeSortNode}
                      disabled={
                        !inputData ||
                        !isTitleValid ||
                        !title.trim() ||
                        inputData.length === 0 ||
                        (mode === "simple" &&
                          fields.filter((f) => f.field.trim()).length === 0) ||
                        (mode === "code" && !customCode.trim())
                      }
                      className="w-full mt-4 sticky bottom-0 left-0 right-0 rounded-b-md"
                      size="sm"
                    >
                      <FlaskConical size={14} className="mr-2" />
                      Execute step
                    </Button>
                  </div>
                </div>
              </NodeContentSection>
            </div>

            <PanelDivider onMouseDown={startDrag(1)} />

            {/* Right Section - Response (4/12 columns) */}
            <div style={{ flex: 1, minWidth: 80 }}>
              <NodeContentSection scrollable={false} hasRelative={true}>
                <ResponseViewer
                  response={errorResponse || outputData}
                  loading={loading}
                  emptyMessage="Execute the SORT to see output preview"
                  loadingMessage="Processing..."
                  onCopy={handleCopyOutput}
                  isError={!!errorResponse}
                  isLoopChild={(data as any).is_loop_child || false}
                  totalIterations={(data as any).total_iterations || 0}
                  iterations={(data as any).iterations || []}
                  fillParent={true}
                  onResponseChange={(newData) => {
                    setOutputData(newData);
                    data.onDataChange?.(id, {
                      response: newData,
                      manuallyEdited: true,
                    });
                  }}
                />
              </NodeContentSection>
            </div>
          </div>
        </div>
      )}
    </FullscreenWrapper>
  );
};

export default SortNode;
